import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, StickyPageHead } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Sparkles } from "lucide-react";
import ContactPreferencesCard from "@/components/ContactPreferencesCard";
import CategoryManager from "@/components/CategoryManager";
import VatRateManager from "@/components/VatRateManager";
import DataBackup from "@/components/DataBackup";
import SubscriptionCard from "@/components/SubscriptionCard";
import BillingCard from "@/components/BillingCard";
import PaymentMethodsCard from "@/components/PaymentMethodsCard";
import { startProductTour } from "@/lib/productTour";

export default function Settings() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const handleStartTour = () => {
    // İlk adımın hedefi Dashboard'da; başka sayfadaysa önce ana sayfaya git.
    if (location.pathname !== "/") {
      navigate("/");
      // DOM yerleşene kadar bir tık bekle.
      setTimeout(() => startProductTour(), 250);
    } else {
      startProductTour();
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if ((next ?? []).length < 4) { toast.error("Yeni şifre en az 4 karakter olmalı"); return; }
    if (next !== confirm) { toast.error("Yeni şifre tekrarı eşleşmiyor"); return; }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Şifre güncellendi");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="settings-page">
      <StickyPageHead>
        <PageHeader title="Ayarlar" subtitle="Hesabınızı ve uygulama tercihlerinizi yönetin" />
      </StickyPageHead>

      <div className="max-w-3xl space-y-6">
      <SectionCard title="Hesap">
        <div className="p-5 space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <div className="text-slate-600">Kullanıcı adı</div>
            <div className="font-medium text-slate-900" data-testid="settings-username">{user?.username}</div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-500 pt-2 border-t border-slate-100">
            <ShieldCheck className="w-4 h-4 text-emerald-600" />
            Tek kullanıcılı özel sistem.
          </div>
        </div>
      </SectionCard>

      <SubscriptionCard onRefreshAuth={checkAuth} />

      <PaymentMethodsCard />

      <BillingCard />

      <SectionCard title="Şifre Değiştir">
        <form onSubmit={onSubmit} className="p-5 space-y-4" data-testid="change-password-form">
          <div className="space-y-1.5">
            <Label className="text-xs">Mevcut Şifre</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} required className="h-10 rounded-lg" data-testid="current-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Yeni Şifre</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} required className="h-10 rounded-lg" data-testid="new-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Yeni Şifre (Tekrar)</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="h-10 rounded-lg" data-testid="confirm-password-input" />
          </div>
          <div className="flex justify-end">
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800" disabled={loading} data-testid="change-password-button">
              {loading ? "Güncelleniyor..." : "Şifreyi Güncelle"}
            </Button>
          </div>
        </form>
      </SectionCard>

      <CategoryManager />

      <VatRateManager />

      <ContactPreferencesCard />

      <SectionCard title="Penz'i Keşfet">
        <div className="p-5 flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900">Penz&apos;i Keşfet</div>
            <div className="text-xs text-slate-500 mt-1">
              Penz&apos;in temel özelliklerini ürün turuyla yeniden görüntüleyin.
            </div>
          </div>
          <Button
            type="button"
            onClick={handleStartTour}
            className="bg-slate-900 hover:bg-slate-800 shrink-0"
            data-testid="settings-start-tour"
          >
            Ürün Turunu Başlat
          </Button>
        </div>
      </SectionCard>

      <DataBackup />
      </div>
    </div>
  );
}
